/**
 * Schema of available paths per data source for the layout editor.
 *
 * Used to drive path selection (no free-text) in the property panel.
 * Derived from the TypeScript types declared in `./types.ts`, themselves
 * grounded in the Prisma remote schema (English field names) and the layout
 * domain types (`AthleteData`, `GroupData`, `MeetData`, `NextLiftData`, ...).
 */

import type { DataSourceId } from "./types.js";

/**
 * Optional scalar (or nested object) type for a path entry.
 *
 * Layout `DATA_SOURCE_PATHS` omit this and the editor infers from mock data.
 * Custom-discipline overlays set it so `get` output ports match field types.
 */
export type PathEntryValueType =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "list"
  | "object";

export interface PathEntry {
  /** Dot-path, e.g. "lastname" or "weightCategory.name". */
  path: string;
  /** Optional human-facing label used by editor dropdowns. */
  label?: string;
  /** Nested fields (for objects). */
  children?: PathEntry[];
  /**
   * Type of the value at this path. When set, the behavior `get` operator
   * uses it for the output port instead of inferring from mock/runtime data.
   */
  valueType?: PathEntryValueType;
}

/** For scalar sources (timer, currentMovement, ...), use null. */
export type DataSourcePathSchema = PathEntry[] | null;

// ---------------------------------------------------------------------------
// Reusable sub-schemas (relations exposed inside Athlete / Record / Level)
// ---------------------------------------------------------------------------

/** Scalar age-category fields shared by lists, references and nested objects. */
export const AGE_CATEGORY_FIELDS: PathEntry[] = [
  { path: "id", valueType: "number" },
  { path: "name", valueType: "string" },
  { path: "recordName", valueType: "string" },
  { path: "code", valueType: "string" },
  { path: "ageMin", valueType: "number" },
  { path: "ageMax", valueType: "number" },
  { path: "disciplineId", valueType: "number" },
  { path: "organisationId", valueType: "string" },
  { path: "displayOrder", valueType: "number" },
  { path: "sex", valueType: "string" },
  { path: "isSenior", valueType: "boolean" },
  { path: "isOpen", valueType: "boolean" },
  { path: "isHandisport", valueType: "boolean" },
  { path: "priority", valueType: "number" },
];

/** Scalar weight-category fields shared by lists, references and nested objects. */
export const WEIGHT_CATEGORY_FIELDS: PathEntry[] = [
  { path: "id", valueType: "number" },
  { path: "name", valueType: "string" },
  { path: "minWeight", valueType: "number" },
  { path: "maxWeight", valueType: "number" },
  { path: "isJunior", valueType: "boolean" },
  { path: "sex", valueType: "string" },
  { path: "disciplineId", valueType: "number" },
  { path: "ageCategoryId", valueType: "number" },
  { path: "organisationId", valueType: "string" },
  { path: "displayOrder", valueType: "number" },
  { path: "isOpen", valueType: "boolean" },
  { path: "isHandisport", valueType: "boolean" },
  { path: "minInclusive", valueType: "boolean" },
  { path: "maxInclusive", valueType: "boolean" },
];

const DISCIPLINE_FIELDS: PathEntry[] = [
  { path: "id" },
  { path: "name" },
  { path: "code" },
  { path: "competitionFormat" },
];

const LIGUE_FIELDS: PathEntry[] = [{ path: "id" }, { path: "name" }];

/** Scalar Organisation fields (layout source + nested on each record). */
export const ORGANISATION_PATHS: PathEntry[] = [
  { path: "id", valueType: "string" },
  { path: "name", valueType: "string" },
  { path: "code", valueType: "string" },
  { path: "countryCode", valueType: "string" },
  { path: "recordLabel", valueType: "string" },
  { path: "recordColor", valueType: "string" },
  { path: "recordBadgeUrl", valueType: "string" },
  { path: "parentId", valueType: "string" },
];

const POWERSCOUT_BESTS_FIELDS: PathEntry[] = [
  { path: "best_squat_kg" },
  { path: "best_bench_kg" },
  { path: "best_deadlift_kg" },
  { path: "best_total_kg" },
];

// ---------------------------------------------------------------------------
// Athlete (currentAthlete + each item of `athletes`)
// ---------------------------------------------------------------------------

const ATHLETE_PATHS: PathEntry[] = [
  { path: "id" },
  { path: "lastname" },
  { path: "firstname" },
  { path: "club" },
  { path: "licence" },
  { path: "sex" },
  { path: "birthDate" },
  { path: "bodyweight" },
  { path: "lot" },
  { path: "group_id" },
  { path: "isDisqualified" },

  // Squat
  { path: "squat1" },
  { path: "squat1Status" },
  { path: "squat1Auto" },
  { path: "squat1Overturn" },
  { path: "squat1LeftColor" },
  { path: "squat1ChiefColor" },
  { path: "squat1RightColor" },
  { path: "squat1OverturnColor" },
  { path: "squat2" },
  { path: "squat2Status" },
  { path: "squat2Auto" },
  { path: "squat2Overturn" },
  { path: "squat2LeftColor" },
  { path: "squat2ChiefColor" },
  { path: "squat2RightColor" },
  { path: "squat2OverturnColor" },
  { path: "squat3" },
  { path: "squat3Status" },
  { path: "squat3Auto" },
  { path: "squat3Overturn" },
  { path: "squat3LeftColor" },
  { path: "squat3ChiefColor" },
  { path: "squat3RightColor" },
  { path: "squat3OverturnColor" },

  // Bench
  { path: "bench1" },
  { path: "bench1Status" },
  { path: "bench1Auto" },
  { path: "bench1Overturn" },
  { path: "bench1LeftColor" },
  { path: "bench1ChiefColor" },
  { path: "bench1RightColor" },
  { path: "bench1OverturnColor" },
  { path: "bench2" },
  { path: "bench2Status" },
  { path: "bench2Auto" },
  { path: "bench2Overturn" },
  { path: "bench2LeftColor" },
  { path: "bench2ChiefColor" },
  { path: "bench2RightColor" },
  { path: "bench2OverturnColor" },
  { path: "bench3" },
  { path: "bench3Status" },
  { path: "bench3Auto" },
  { path: "bench3Overturn" },
  { path: "bench3LeftColor" },
  { path: "bench3ChiefColor" },
  { path: "bench3RightColor" },
  { path: "bench3OverturnColor" },

  // Deadlift
  { path: "deadlift1" },
  { path: "deadlift1Status" },
  { path: "deadlift1Auto" },
  { path: "deadlift1Overturn" },
  { path: "deadlift1LeftColor" },
  { path: "deadlift1ChiefColor" },
  { path: "deadlift1RightColor" },
  { path: "deadlift1OverturnColor" },
  { path: "deadlift2" },
  { path: "deadlift2Status" },
  { path: "deadlift2Auto" },
  { path: "deadlift2Overturn" },
  { path: "deadlift2LeftColor" },
  { path: "deadlift2ChiefColor" },
  { path: "deadlift2RightColor" },
  { path: "deadlift2OverturnColor" },
  { path: "deadlift3" },
  { path: "deadlift3Status" },
  { path: "deadlift3Auto" },
  { path: "deadlift3Overturn" },
  { path: "deadlift3LeftColor" },
  { path: "deadlift3ChiefColor" },
  { path: "deadlift3RightColor" },
  { path: "deadlift3OverturnColor" },

  // Results & loader
  { path: "totalkg" },
  { path: "totalAnnonced" },
  { path: "rank" },
  { path: "squatHeight" },
  { path: "benchHeight" },
  { path: "secuHeight" },
  { path: "cales" },
  { path: "rackPosition" },
  { path: "ageCategoryId" },
  { path: "weightCategoryId" },
  { path: "points" },
  { path: "disciplineId" },

  // Relations
  { path: "ageCategory", children: AGE_CATEGORY_FIELDS },
  { path: "weightCategory", children: WEIGHT_CATEGORY_FIELDS },
  { path: "discipline", children: DISCIPLINE_FIELDS },
  { path: "powerscoutBests", children: POWERSCOUT_BESTS_FIELDS },
];

// ---------------------------------------------------------------------------
// Group (currentGroup + each item of `groups`)
// ---------------------------------------------------------------------------

const GROUP_PATHS: PathEntry[] = [
  { path: "id" },
  { path: "name" },
  { path: "meet_id" },
  { path: "plateauNumber" },
  { path: "groupNumber" },
  { path: "observations" },
  { path: "secretary" },
  { path: "referee1" },
  { path: "referee2" },
  { path: "centralReferee" },
  { path: "jury" },
  { path: "share" },
  { path: "competitionFormat" },
  { path: "selected" },
];

// ---------------------------------------------------------------------------
// Meet (currentMeet)
// ---------------------------------------------------------------------------

const MEET_PATHS: PathEntry[] = [
  { path: "id" },
  { path: "organisationId", valueType: "string" },
  { path: "name" },
  { path: "city" },
  { path: "startdate", valueType: "date" },
  { path: "enddate", valueType: "date" },
  { path: "created_at" },
  { path: "ligue_id" },
  { path: "licenseKey" },
  { path: "streamUrl" },
  { path: "isPublic" },
  { path: "isNational" },
  { path: "isOver" },
  { path: "ligue", children: LIGUE_FIELDS },
];

// ---------------------------------------------------------------------------
// refereeLights (item: RefereeLights)
// ---------------------------------------------------------------------------

const REFEREE_LIGHTS_PATHS: PathEntry[] = [
  { path: "type" },
  { path: "passed" },
  { path: "color" },
  { path: "juryEnabled" },
  { path: "profileId", valueType: "string" },
  {
    path: "refereeing",
    valueType: "object",
    children: [
      { path: "votes", valueType: "object" },
      { path: "state", valueType: "object" },
      { path: "result", valueType: "object" },
      { path: "revision", valueType: "number" },
    ],
  },
];

// ---------------------------------------------------------------------------
// nextLiftData (NextLiftData: athlete + movement + attemptNumber)
// ---------------------------------------------------------------------------

const NEXT_LIFT_DATA_PATHS: PathEntry[] = [
  { path: "athlete", children: ATHLETE_PATHS },
  { path: "movement" },
  { path: "attemptNumber" },
];

// ---------------------------------------------------------------------------
// records (item: RecordData) and levels (item: LevelData)
// ---------------------------------------------------------------------------

const RECORD_PATHS: PathEntry[] = [
  { path: "id" },
  { path: "movement" },
  { path: "weight" },
  { path: "ageCategoryId" },
  { path: "weightCategoryId" },
  { path: "dateLocation" },
  { path: "athleteName" },
  { path: "disciplineId" },
  { path: "sex" },
  { path: "sheetDate" },
  { path: "organisationId", valueType: "string" },
  { path: "ageCategory", children: AGE_CATEGORY_FIELDS },
  { path: "weightCategory", children: WEIGHT_CATEGORY_FIELDS },
  { path: "discipline", children: DISCIPLINE_FIELDS },
  { path: "organisation", valueType: "object", children: ORGANISATION_PATHS },
];

const LEVEL_PATHS: PathEntry[] = [
  { path: "id" },
  { path: "name" },
  { path: "minTotal" },
  { path: "maxTotal" },
  { path: "ageCategoryId" },
  { path: "weightCategoryId" },
  { path: "disciplineId" },
  { path: "sheetDate" },
  { path: "ageCategory", children: AGE_CATEGORY_FIELDS },
  { path: "weightCategory", children: WEIGHT_CATEGORY_FIELDS },
  { path: "discipline", children: DISCIPLINE_FIELDS },
];

// ---------------------------------------------------------------------------
// currentNode (behavior runtime owner node geometry)
// ---------------------------------------------------------------------------

const CURRENT_NODE_PATHS: PathEntry[] = [
  { path: "x" },
  { path: "y" },
  { path: "width" },
  { path: "height" },
  { path: "rotation" },
  { path: "skewX" },
  { path: "skewY" },
];

// ---------------------------------------------------------------------------
// themeColors (ThemeColors)
// ---------------------------------------------------------------------------

const THEME_COLORS_PATHS: PathEntry[] = [
  { path: "primary" },
  { path: "primaryText" },
  { path: "background" },
  { path: "backgroundText" },
  { path: "rankingText" },
  { path: "ranking" },
];

// ---------------------------------------------------------------------------
// DATA_SOURCE_PATHS - exhaustive map of every InjectedData source
// ---------------------------------------------------------------------------

export const DATA_SOURCE_PATHS: Record<DataSourceId, DataSourcePathSchema> = {
  behaviorTimers: null,
  behaviorTimerScope: null,
  __customDisciplineLabels: null,
  currentValue: null,
  athlete: null,
  selectedRowId: null,
  selectedColumnKey: null,

  // Live competition data
  currentAthlete: ATHLETE_PATHS,
  athletes: ATHLETE_PATHS,
  group: GROUP_PATHS,
  currentGroup: GROUP_PATHS,
  groups: GROUP_PATHS,
  currentMeet: MEET_PATHS,

  // Timing & current attempt
  timer: null,
  timerSeconds: null,
  attemptTimers: null,
  attemptTimerSeconds: null,
  currentMovement: null,
  attemptNumber: null,
  placementIfSuccessful: null,

  // Referees & lifts
  refereeLights: REFEREE_LIGHTS_PATHS,
  nextLiftData: NEXT_LIFT_DATA_PATHS,

  // Reference catalogs
  records: RECORD_PATHS,
  levels: LEVEL_PATHS,
  disciplineAgeCategories: AGE_CATEGORY_FIELDS,
  disciplineWeightCategories: WEIGHT_CATEGORY_FIELDS,
  ageCategories: AGE_CATEGORY_FIELDS,
  weightCategories: WEIGHT_CATEGORY_FIELDS,
  disciplines: DISCIPLINE_FIELDS,
  leagues: LIGUE_FIELDS,
  organisation: ORGANISATION_PATHS,
  organisations: ORGANISATION_PATHS,

  // Theming
  themeColors: THEME_COLORS_PATHS,
  displaySettings: [
    {
      path: "automaticRefereeing",
      label: "Arbitrage automatique",
      valueType: "boolean",
    },
    { path: "lightsEnabled", label: "Arbitrage activé", valueType: "boolean" },
    { path: "showLights", label: "Afficher l’arbitrage", valueType: "boolean" },
    {
      path: "webClientUrl",
      label: "URL de l’application web",
      valueType: "string",
    },
  ],

  // Context values (injected at runtime by the renderer)
  currentNode: CURRENT_NODE_PATHS,
  rowIndex: null,
  layoutListRowItem: null,
  sortAIndex: null,
  sortBIndex: null,
  listTransitionSignal: null,

  // Custom-discipline modal/cell runtime state (not part of the generic
  // layout editor palette; typed via `buildCustomDisciplineObjectPathSchema`
  // overlays instead).
  modalValues: null,
  inputValues: null,

  // Internal iteration scope - injected only by generic list operators
  // (`findByCondition`, `filter`, `sort`) and consumed only by `currentItem`
  // / `comparator`. Intentionally left without paths and not declared as
  // scalar sources so they never appear in the variable palette nor the
  // mock-data panel.
  iterationItem: null,
  iterationIndex: null,
  iterationItemA: null,
  iterationItemB: null,
};

/**
 * Returns whether the given source has selectable paths (object with fields).
 */
export function sourceHasPaths(source: DataSourceId): boolean {
  const schema = DATA_SOURCE_PATHS[source];
  return Array.isArray(schema) && schema.length > 0;
}

/**
 * Primitive value types for scalar data sources (no sub-paths).
 * Used to assign the correct output port type when creating a source-bound
 * variable or event node.
 */
export const SCALAR_SOURCE_VALUE_TYPES: Partial<
  Record<DataSourceId, "string" | "number" | "boolean" | "date">
> = {
  timer: "string",
  timerSeconds: "number",
  currentMovement: "string",
  attemptNumber: "number",
  placementIfSuccessful: "number",
  currentValue: "string",
  rowIndex: "number",
};

/**
 * Returns the primitive value type for a scalar source, or null if the source
 * is not scalar.
 */
export function getScalarSourceValueType(
  source: DataSourceId,
): "string" | "number" | "boolean" | "date" | null {
  return SCALAR_SOURCE_VALUE_TYPES[source] ?? null;
}

export interface FlattenedPathOption {
  value: string;
  label: string;
}

export interface FlattenPathOptions {
  includeChildren?: boolean;
}

/**
 * When a field path points to a nested object in {@link DATA_SOURCE_PATHS}
 * (entry with `children`), returns `"object"`. Used by the behavior editor to
 * type `get` operator outputs when mock/runtime values are `null`.
 */
export function getSchemaValueTypeForFieldPath(
  source: DataSourceId,
  fieldPath?: string,
  objectFieldPathBase?: string,
): "object" | null {
  if (!fieldPath) {
    return null;
  }
  const fullPath = objectFieldPathBase
    ? `${objectFieldPathBase}.${fieldPath}`
    : fieldPath;
  const segments = fullPath.split(".").filter(Boolean);
  if (segments.length === 0) {
    return null;
  }

  const rootSchema = DATA_SOURCE_PATHS[source];
  if (!rootSchema?.length) {
    return null;
  }

  let currentSchema: PathEntry[] | null = rootSchema;
  for (let i = 0; i < segments.length; i++) {
    if (!currentSchema?.length) {
      return null;
    }
    const entry: PathEntry | undefined = currentSchema.find(
      (item) => item.path === segments[i],
    );
    if (!entry) {
      return null;
    }
    if (i === segments.length - 1) {
      return entry.children?.length ? "object" : null;
    }
    currentSchema = entry.children ?? null;
  }
  return null;
}

export function getPathSchemaAtPath(
  schema: PathEntry[] | null,
  path?: string,
): PathEntry[] | null {
  if (!schema?.length) return null;
  if (!path) return schema;

  const segments = path.split(".").filter(Boolean);
  if (segments.length === 0) return schema;

  let currentSchema: PathEntry[] | null = schema;
  for (const segment of segments) {
    if (!currentSchema?.length) return null;
    const entry: PathEntry | undefined = currentSchema.find(
      (item: PathEntry) => item.path === segment,
    );
    if (!entry) return null;
    currentSchema = entry.children ?? null;
  }
  return currentSchema;
}

/**
 * Returns the path entry at a dotted path, or null when any segment is missing.
 *
 * @param schema - Root path entries for a source.
 * @param path - Dotted path such as `"name"` or `"weightCategory.name"`.
 * @returns The matching entry, or null.
 */
export function getPathEntryAtPath(
  schema: PathEntry[] | null,
  path?: string,
): PathEntry | null {
  if (!schema?.length || !path) {
    return null;
  }
  const segments = path.split(".").filter(Boolean);
  if (segments.length === 0) {
    return null;
  }
  let currentSchema: PathEntry[] | null = schema;
  let entry: PathEntry | undefined;
  for (const segment of segments) {
    if (!currentSchema?.length) {
      return null;
    }
    entry = currentSchema.find((item) => item.path === segment);
    if (!entry) {
      return null;
    }
    currentSchema = entry.children ?? null;
  }
  return entry ?? null;
}

/**
 * Resolves the `get` output type from a path schema.
 *
 * Prefers an explicit `valueType` on the leaf entry. Nested entries with
 * children and no `valueType` resolve to `"object"`.
 *
 * @param schema - Root path entries for a source (layout or custom overlay).
 * @param fieldPath - Selected field on the `get` node.
 * @param objectFieldPathBase - Optional prefix when `get` is nested.
 * @returns The value type, or null when the editor should infer from mock data.
 */
export function getPathEntryValueType(
  schema: PathEntry[] | null,
  fieldPath?: string,
  objectFieldPathBase?: string,
): PathEntryValueType | null {
  const fullPath = objectFieldPathBase
    ? `${objectFieldPathBase}.${fieldPath ?? ""}`
    : fieldPath;
  const entry = getPathEntryAtPath(schema, fullPath);
  if (!entry) {
    return null;
  }
  if (entry.valueType) {
    return entry.valueType;
  }
  if (entry.children?.length) {
    return "object";
  }
  return null;
}

/**
 * Flattens a path schema into a list of options for a dropdown.
 * Nested paths are prefixed (e.g. "weightCategory.name" with label
 * "weightCategory → name").
 */
export function getFlattenedPathOptions(
  schema: PathEntry[] | null,
  options?: FlattenPathOptions,
): FlattenedPathOption[] {
  if (!schema?.length) return [];
  const includeChildren = options?.includeChildren ?? true;
  const out: FlattenedPathOption[] = [];
  for (const entry of schema) {
    if (includeChildren && entry.children?.length) {
      for (const child of entry.children) {
        const fullPath = `${entry.path}.${child.path}`;
        out.push({
          value: fullPath,
          label: `${entry.label ?? entry.path} → ${child.label ?? child.path}`,
        });
      }
    } else {
      out.push({ value: entry.path, label: entry.label ?? entry.path });
    }
  }
  return out;
}
